import type { Field as HandlerField } from '~/features/document-handler/types/fields';
import type { useFormatDateReturnType } from '~/i18n/hooks/useFormatDate/types';
import type { useFormatNumberReturnType } from '~/i18n/hooks/useFormatNumber/types';

import { EM_DASH } from '~/common/constants/unicode';
import { PilotId as EdsPilotId } from '~/eds/src/types/PilotId';
import { DataFieldType } from '~/enums/DataFieldType';
import { Fields } from '~/features/document-handler/types/fields';
import { pilot } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { getUniqueFieldValues } from '~/utils/document.utils';
import { capitalizeWords } from '~/utils/strings';

import type { Field, FieldValue } from './types';

type Section = {
  title: string;
  fields: Field[];
};
export type DocumentInformationResponse = {
  date_modified: string;
  id: PilotId;
  is_wss_contract?: boolean;
  is_procurement_contract_ready?: boolean | null;
  sections: Section[];
  ticket_id: string | null;
};

export const getDocumentFields = async ({
  documentId,
  formatDate,
  formatNumber,
}: {
  documentId: EdsPilotId;
  formatDate: useFormatDateReturnType;
  formatNumber: useFormatNumberReturnType;
}): Promise<Fields> => {
  const response: DocumentInformationResponse = await pilot.get(
    `/document/${documentId}/document-information/`,
  );

  return mapResponse(response, formatDate, formatNumber);
};

export const mapResponse = (
  response: DocumentInformationResponse,
  formatDate: useFormatDateReturnType,
  formatNumber: useFormatNumberReturnType,
): Fields => {
  return {
    dateModified: response.date_modified,
    documentId: response.id,
    ticketId: response.ticket_id,
    isWssContract: response.is_wss_contract,
    isProcurementContractReady: response.is_procurement_contract_ready,
    sections: response.sections.map((section) => ({
      title: section.title,
      fields: section.fields.map((field) => {
        let value: HandlerField['value'];
        if (!field.value) {
          value = field.value;
        } else if (Array.isArray(field.value)) {
          value = field.value.map((v) => ({
            displayValue: v.display_value,
            value: v.value,
          }));
        } else {
          value = {
            displayValue: field.value.display_value,
            value: field.value.value,
          };
        }

        return {
          allowNewOptions: field.allow_new_options,
          className: field.class_name,
          coordinates: field.coordinates,
          fileTypeRestrictions: field.file_type_restrictions,
          helpText: field.help_text,
          source: field.source,
          htmlTokens: field.html_tokens,
          id: field.id,
          isBeta: field.is_beta,
          isEditable: field.is_editable,
          isFlagged: field.is_flagged,
          isNullable: field.is_nullable,
          name: field.name,
          type: field.type,
          value,
          displayValue: `${getFieldValue(
            field.type,
            field.value,
            formatDate,
            formatNumber,
          )}`,
          values: field.values,
          staticKeyName: field.static_key_name,
          uf_field_id: field.uf_field_id,
          uf_field_value: field.uf_field_value,
        };
      }),
    })),
  };
};

export const getFieldValue = (
  type: DataFieldType,
  value: Nullable<FieldValue | FieldValue[]>,
  formatDate: useFormatDateReturnType,
  formatNumber: useFormatNumberReturnType,
) => {
  if (!value) return EM_DASH;

  switch (type) {
    case DataFieldType.ARRAY_MULTIPLE:
    case DataFieldType.ATTACHMENT:
      if (!(value as FieldValue[]).length) return EM_DASH;
      return getUniqueFieldValues(value as FieldValue[])
        .map((val) => `- ${val.display_value}`)
        .join('\n');

    case DataFieldType.DATE:
      const dateValue = (value as FieldValue).display_value;
      if (!dateValue) return EM_DASH;
      return formatDate(dateValue, 'long');

    case DataFieldType.NUMBER:
      const numberValue = Number((value as FieldValue).value);
      if (!numberValue) return EM_DASH;
      return formatNumber(numberValue);

    case DataFieldType.BOOLEAN:
      if (!(value as FieldValue).display_value) return EM_DASH;

      return capitalizeWords((value as FieldValue).display_value.toString());

    default:
      return (value as FieldValue).display_value || EM_DASH;
  }
};
